import { IconButton } from '@creditornot/cb-button';
import { Cross, ImageAdd } from '@creditornot/cb-icons';
import { colors, cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import React from 'react';
import styled from 'styled-components';

import { CustomDashedBorderCss } from '../../utils/CustomDashedBorderCss';

export type ImageSlotProps = {
  iconSize?: number;
  imageUrl?: string;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isOver?: boolean;
  onUnlink?: () => void;
};

const Root = styled.div<{ isActive?: boolean; borderRadius: number }>`
  width: 100%;
  height: 100%;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 100ms ease-in;
  transform: ${({ isActive }) => `scale(${isActive ? 1.1 : 1})`};
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  margin: 4px 0;
  overflow: hidden;
  position: relative;
`;

const Image = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const Loader = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${cssThemedValue({ light: colors.pepper76, dark: colors.salt80 })};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const EmptySlot = styled.div<{ isActive: boolean; borderRadius: number }>`
  ${CustomDashedBorderCss};
  width: 100%;
  height: 100%;
  fill: ${({ isActive }) =>
    isActive ? colors.wolt100 : cssThemedValue({ light: colors.pepper40, dark: colors.salt40 })};
  background-color: ${({ isActive }) =>
    isActive
      ? cssThemedValue({ light: colors.wolt8, dark: colors.wolt12 })
      : cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropIcon = styled.div<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;

const UnlinkButton = styled(IconButton).attrs({ size: 'tiny', variant: 'white' })`
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: ${cssThemedValue({ light: colors.pepper76, dark: colors.salt80 })};
  opacity: 0;
  transition: opacity ease-in 100ms;

  ${Root}:hover & {
    opacity: 1;
  }
`;

const borderRadius = 8;
export const ImageSlot = ({
  iconSize = 32,
  imageUrl,
  isReadOnly = false,
  isLoading = false,
  isOver = false,
  onUnlink,
}: ImageSlotProps) => (
  <Root isActive={isOver} borderRadius={borderRadius}>
    {imageUrl ? (
      <ImageContainer>
        {isLoading && (
          <Loader data-testid="image-slot__loader">
            <Spinner size="medium" />
          </Loader>
        )}

        {!isReadOnly && onUnlink && (
          <UnlinkButton onClick={onUnlink}>
            <Cross />
          </UnlinkButton>
        )}

        <Image src={imageUrl} />
      </ImageContainer>
    ) : (
      <EmptySlot isActive={isOver} borderRadius={borderRadius} data-testid="image-slot__empty-slot">
        <DropIcon size={iconSize}>
          <ImageAdd />
        </DropIcon>
      </EmptySlot>
    )}
  </Root>
);
